import {
  createTheme,
  MantineProvider,
  Modal,
  Popover,
  Portal,
  Switch,
  Table,
  PasswordInput,
  Select,
  MultiSelect,
  Input,
  InputBase,
  ActionIcon,
  type MantineThemeOverride,
} from "@mantine/core";
import React, { type ReactNode, useMemo } from "react";
import { useTheme } from "src/hooks";

interface ThemeWrapperProps {
  children: ReactNode;
}

const ThemeWrapper: React.FC<ThemeWrapperProps> = ({ children }) => {
  const { colorScheme, colors, primaryColor, hideHeader } = useTheme();

  const mantineTheme = useMemo(
    () =>
      createTheme({
        primaryColor,
        colors: { ...colors } as unknown as MantineThemeOverride["colors"],
        ...(hideHeader !== undefined && { other: { hideHeader } }),
        components: {
          Modal: Modal.extend({
            defaultProps: {
              withinPortal: true,
              keepMounted: false,
            },
          }),
          Portal: Portal.extend({
            defaultProps: {
              target: "#modals-root",
              reuseTargetNode: false,
            },
          }),
          Popover: Popover.extend({
            defaultProps: {
              hideDetached: false,
            },
          }),
          Switch: Switch.extend({
            defaultProps: {
              withThumbIndicator: false,
            },
          }),
          Table: Table.extend({
            styles: {
              table: {
                fontSize: "var(--mantine-font-size-sm)",
              },
            },
          }),
          PasswordInput: PasswordInput.extend({
            styles: {
              input: {
                fontSize: "var(--mantine-font-size-sm)",
              },
            },
          }),
          Select: Select.extend({
            defaultProps: {
              withCheckIcon: false,
            },
          }),
          MultiSelect: MultiSelect.extend({
            defaultProps: {
              withCheckIcon: false,
            },
          }),
          Input: Input.extend({
            defaultProps: {
              rightSectionPointerEvents: "all",
            },
          }),
          InputBase: InputBase.extend({
            defaultProps: {
              rightSectionPointerEvents: "all",
            },
          }),
          ActionIcon: ActionIcon.extend({
            defaultProps: {
              variant: "subtle",
              color: "var(--mantine-color-main-9)",
            },
          }),
        },
      }),
    [colors, primaryColor, hideHeader],
  );

  return (
    <MantineProvider
      theme={mantineTheme}
      defaultColorScheme={colorScheme}
      forceColorScheme={colorScheme}>
      {children}
    </MantineProvider>
  );
};

export default ThemeWrapper;
